import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { useEffect, useState } from "react";
import { arrayMap } from "./array.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateCarInfoMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createCarInfoApi(data) {
    try {
      const api = `/car-info`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createCarInfoApi", ...keyData],
    mutationFn: (data) => createCarInfoApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("car-infos");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createCarInfo: mutateAsync };
}

export function useUpdateCarInfoMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateCarInfoApi(data) {
    try {
      const api = `/car-info`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateCarInfoApi", ...keyData],
    mutationFn: (data) => updateCarInfoApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("car-infos");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateCarInfo: mutateAsync };
}

export function useDeleteCarInfoMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteCarInfoApi(id) {
    try {
      const api = `/car-info/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteCarInfoApi", ...keyData],
    mutationFn: (data) => deleteCarInfoApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("car-infos");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deleteCarInfo: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllCarInfoQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getAllCarInfoApi(query) {
    try {
      const api = `/car-infos${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey: [`getAllCarInfo-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getAllCarInfoApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  const parents = data?.data?.parents ?? [];
  return { data: newData, parents, loading, ...rest };
}

export function useGetGroupCarInfoQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getGroupCarInfoApi(query) {
    try {
      const api = `/car-infos/group${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey: [`getGroupCarInfo-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getGroupCarInfoApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  return { data: newData, loading, ...rest };
}

// get all car info by car info type slug
export function useGetAllCarInfoByCarInfoTypeSlug(slug) {
  const [dropdowns, setDropdowns] = useState([]);
  const {
    isLoading: loading,
    data,
    ...rest
  } = useGetAllCarInfoQuery({
    query: `?car_info_type_slug=${slug}`,
    key: `car_info_type_slug-${slug}`
  });

  useEffect(() => {
    if (data?.length) {
      let newData = arrayMap(data, (item) => ({ label: item?.name, value: item?.id })) || [];
      newData.push({ label: "Others", value: "others" });
      setDropdowns(newData);
    }
  }, [data]);

  return { data: dropdowns, loading, rest };
}
